import React, { useState } from 'react';
import { toast } from 'react-toastify';
import NeoCard from '../../../../../components/Common/NeoCard';
import NeoButton from '../../../../../components/Common/NeoButton';
import NeoInput from '../../../../../components/Common/NeoInput';
import {
    CONTACT_OUTCOME,
    CONTACT_OUTCOME_OPTIONS,
    CONTACT_REASON,
    LOSS_REASON_OPTIONS
} from '../../../../../constants/tasks';
import TaskService from '../../../../../services/task_service';

// Outcomes exclusivos do funil de experimental — hidden para outros cards
const TRIAL_ONLY_OUTCOMES = new Set([CONTACT_OUTCOME.TRIAL_SCHEDULED, CONTACT_OUTCOME.TRIAL_CONFIRMED]);

const SidebarRegisterOutcome = ({ item, idTenant, user, onRefresh }) => {
    const [selectedOutcome, setSelectedOutcome] = useState('');
    const [outcomeNotes, setOutcomeNotes] = useState('');
    const [followUpDate, setFollowUpDate] = useState('');
    const [lossReason, setLossReason] = useState('');
    const [isSubmittingOutcome, setIsSubmittingOutcome] = useState(false);

    const isCompleted = item?.status === 'done' || item?.status === 'archived';
    if (isCompleted) return null;

    const isTrialFollowup = item?.contactReason === CONTACT_REASON.TRIAL_FOLLOWUP;

    // Filtra o dropdown: outcomes de experimental só aparecem para cards trial_followup
    const availableOptions = CONTACT_OUTCOME_OPTIONS.filter(opt =>
        isTrialFollowup ? true : !TRIAL_ONLY_OUTCOMES.has(opt.value)
    );

    const needsFollowUpDate = [
        CONTACT_OUTCOME.ASKED_DEADLINE,
        CONTACT_OUTCOME.CALLBACK_LATER,
        CONTACT_OUTCOME.SCHEDULED_VISIT,
        CONTACT_OUTCOME.REQUESTED_DISCOUNT
    ].includes(selectedOutcome);

    // Data obrigatória ao agendar, opcional ao confirmar
    const needsTrialDateRequired = selectedOutcome === CONTACT_OUTCOME.TRIAL_SCHEDULED;
    const needsTrialDateOptional = selectedOutcome === CONTACT_OUTCOME.TRIAL_CONFIRMED;

    const needsLossReason = selectedOutcome === CONTACT_OUTCOME.GAVE_UP;

    const handleRegisterOutcome = async () => {
        if (!selectedOutcome || !idTenant || !user) return;

        if (needsFollowUpDate && !followUpDate) {
            toast.warning("Informe a data de retorno para este resultado.");
            return;
        }

        if (needsTrialDateRequired && !followUpDate) {
            toast.warning("Informe a data da aula experimental.");
            return;
        }

        if (selectedOutcome === CONTACT_OUTCOME.GAVE_UP && !lossReason) {
            toast.warning("Informe o motivo da desistência.");
            return;
        }

        setIsSubmittingOutcome(true);
        try {
            const result = await TaskService.registerOutcome(idTenant, user, item.id, {
                outcome: selectedOutcome,
                notes: outcomeNotes,
                followUpDate,
                lossReason: selectedOutcome === CONTACT_OUTCOME.GAVE_UP ? lossReason : null,
            });

            const messages = {
                rescheduled: "Resultado registrado! Card reagendado automaticamente.",
                waiting: "Combinado! Card movido para Aguardando Retorno.",
                converted: "Parabéns! Conversão registrada com sucesso!",
                lost: "Resultado registrado. Motivo de perda salvo para análise.",
                archived_no_response: "Card arquivado por falta de resposta.",
                max_attempts: "3 tentativas esgotadas. Card movido para avaliação.",
                trial_scheduled: "Aula experimental agendada! Aguardando a data.",
                trial_confirmed: "Presença confirmada! Aguardando a aula.",
                updated: "Resultado registrado com sucesso!"
            };
            toast.success(messages[result.action] || messages.updated);

            setSelectedOutcome('');
            setOutcomeNotes('');
            setFollowUpDate('');
            setLossReason('');
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error("Erro ao registrar resultado:", error);
            toast.error("Erro ao registrar resultado.");
        } finally {
            setIsSubmittingOutcome(false);
        }
    };

    const getOutcomeLabel = () => {
        if (
            selectedOutcome === CONTACT_OUTCOME.ENROLLED ||
            selectedOutcome === CONTACT_OUTCOME.RENEWED ||
            selectedOutcome === CONTACT_OUTCOME.RETAINED ||
            selectedOutcome === CONTACT_OUTCOME.PAID ||
            selectedOutcome === CONTACT_OUTCOME.RESOLVED
        ) return 'Registrar Conversão';
        if (selectedOutcome === CONTACT_OUTCOME.FROZEN) return 'Registrar Trancamento';
        if (selectedOutcome === CONTACT_OUTCOME.GAVE_UP) return 'Registrar Perda';
        if (selectedOutcome === CONTACT_OUTCOME.NO_RESPONSE_ARCHIVED) return 'Arquivar por Inatividade';
        if (selectedOutcome === CONTACT_OUTCOME.NO_ANSWER) return 'Salvar e Reagendar';
        if (selectedOutcome === CONTACT_OUTCOME.TRIAL_SCHEDULED) return 'Confirmar Agendamento';
        if (selectedOutcome === CONTACT_OUTCOME.TRIAL_CONFIRMED) return 'Confirmar Presença';
        if (needsFollowUpDate) return 'Salvar e Agendar Retorno';
        return 'Registrar Resultado';
    };

    return (
        <NeoCard thinBorder={true} className="mb-4" bodyClassName="p-3" title="Registrar Resultado" headerIcon="mdi-phone-check-outline">
            {/* Dropdown de Resultado */}
            <NeoInput
                type="select"
                value={selectedOutcome}
                onChange={(e) => {
                    setSelectedOutcome(e.target.value);
                    setFollowUpDate('');
                    setLossReason('');
                }}
                options={availableOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                placeholder="O que aconteceu?"
            />

            {/* Campo condicional: Data de retorno (genérico) */}
            {needsFollowUpDate && (
                <div className="mt-3">
                    <NeoInput
                        type="date"
                        label="Data de retorno"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                    />
                </div>
            )}

            {/* Campo condicional: Data da aula experimental (obrigatória ao agendar) */}
            {needsTrialDateRequired && (
                <div className="mt-3">
                    <NeoInput
                        type="date"
                        label="Data da aula experimental *"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                    />
                </div>
            )}

            {/* Campo condicional: Data da aula experimental (opcional ao confirmar) */}
            {needsTrialDateOptional && (
                <div className="mt-3">
                    <NeoInput
                        type="date"
                        label="Data da aula (opcional)"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                    />
                </div>
            )}

            {/* Campo condicional: Motivo de perda */}
            {needsLossReason && (
                <div className="mt-3">
                    <NeoInput
                        type="select"
                        label="Motivo da desistência"
                        value={lossReason}
                        onChange={(e) => setLossReason(e.target.value)}
                        options={LOSS_REASON_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                        placeholder="Selecione o motivo..."
                    />
                </div>
            )}

            {/* Observação rápida */}
            {selectedOutcome && (
                <div className="mt-3">
                    <NeoInput
                        type="text"
                        placeholder='Observação rápida (ex: "Viaja até dia 5")'
                        value={outcomeNotes}
                        onChange={(e) => setOutcomeNotes(e.target.value)}
                    />
                </div>
            )}

            {/* Botão de Salvar */}
            {selectedOutcome && (
                <NeoButton
                    variant="primary"
                    className="w-100 mt-3"
                    loading={isSubmittingOutcome}
                    onClick={handleRegisterOutcome}
                    icon="mdi-check-circle-outline"
                >
                    {getOutcomeLabel()}
                </NeoButton>
            )}
        </NeoCard>
    );
};

export default SidebarRegisterOutcome;
